import { createSelector } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { selectData } from '@ps/core-data-access';
import {
  mapSurveyLocalization,
  SURVEY_WORKFLOW_PAGE_DATA_KEY,
  toSurveyWorkflowPage,
} from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';
import { resolveActiveCoreChildKey } from './utils/core-children.helper';

const { selectCore } = surveyCreationFeature;

export const selectRootSurveyId = createSelector(
  selectCore,
  (state) => state.rootSurveyId,
);

// The row the route is about; unlike `selectSurvey` it does not follow the
// active child, so it stays put while a project's locale is switched.
export const selectRootSurvey = createSelector(selectCore, (state) =>
  state.rootSurveyId
    ? (state.children[state.rootSurveyId]?.survey ?? null)
    : null,
);

export const selectRootSurveyError = createSelector(selectCore, (state) =>
  state.rootSurveyId
    ? (state.children[state.rootSurveyId]?.error ?? null)
    : null,
);

export const selectActiveChildKey = createSelector(
  selectCore,
  (state) => state.activeChildKey,
);

export const selectKind = createSelector(selectCore, (state) => state.kind);

export const selectTrackerConfig = createSelector(
  selectCore,
  (state) => state.trackerConfig,
);

export const selectCoreChildren = createSelector(
  selectCore,
  (state) => state.children,
);

// Only locales actually participating. participant is null (not false) for a standard survey
// or lone pre-draft locale — "no family" — and still counts as participating; only an explicit
// false (opted out) excludes it.
export const selectParticipantLocales = createSelector(
  selectCoreChildren,
  (children): ReadonlySet<string> =>
    new Set(
      Object.entries(children)
        .filter(([, child]) => child.participant !== false)
        .map(([key, child]) =>
          Option.from(child.survey).mapOr(key, (survey) =>
            mapSurveyLocalization(survey.countryCode, survey.languageShortCode),
          ),
        ),
    ),
);

const selectActiveCoreChild = createSelector(selectCore, (state) => {
  const key = resolveActiveCoreChildKey(state);
  return state.children[key] ?? DEFAULT_CORE_CHILD_STATE;
});

export const selectSurvey = createSelector(
  selectActiveCoreChild,
  ({ survey }) => survey,
);

export const selectSurveyId = createSelector(
  selectSurvey,
  (survey) => survey?.id ?? null,
);

// Which workflow page is open, read off the route's own `data` — never stored
// in this feature, so nothing has to keep a copy in sync.
export const selectSurveyWorkflowPage = createSelector(selectData, (data) =>
  toSurveyWorkflowPage(data[SURVEY_WORKFLOW_PAGE_DATA_KEY]),
);

export const selectIsSurveyLoaded = createSelector(
  selectSurvey,
  (survey) => !!survey,
);

export const selectError = createSelector(
  selectActiveCoreChild,
  ({ error }) => error,
);
